import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Bug,
  Mail,
  Pill,
  UserCheck,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  Send,
  CheckCircle2,
  Search,
  MessageCircle,
  Phone,
  Video,
  Shield,
  Copy,
  LifeBuoy,
  Stethoscope,
} from 'lucide-react';
import { faqCategories, searchFAQs } from '@/utils/faqDatabase';
import healthApi from '@/api/healthApi';
import { toast } from 'sonner';
import AppFooter from '@/components/AppFooter';
import HealthLoopLogo from '@/components/HealthLoopLogo';
import { useBetaFeedback } from '@/contexts/BetaFeedbackContext';
import { Heart } from 'lucide-react';

/**
 * BetaFeedbackSupportSection — the dedicated "Beta Feedback" section on
 * the Support page. Opens the same shared BetaFeedbackModal as every
 * other CTA via the BetaFeedbackContext. Fires an impression event on
 * mount so the Admin Analytics page can attribute support-page
 * conversion separately from dashboard / banner / floating button.
 */
const BetaFeedbackSupportSection = () => {
  const { openFeedbackModal, trackEvent } = useBetaFeedback();
  useEffect(() => {
    trackEvent('impression', 'support_page');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-emerald-50 via-white to-teal-50 border border-emerald-100 px-4 sm:px-5 py-4 sm:py-5"
      data-testid="support-beta-feedback-section"
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <span className="inline-flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-emerald-100 text-emerald-700 flex-shrink-0">
          <Heart className="h-5 w-5 sm:h-6 sm:w-6" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm sm:text-base font-semibold text-gray-900">Beta feedback</p>
          <p className="text-xs sm:text-sm text-gray-600 mt-0.5 leading-relaxed">
            Thank you for helping us test HealthLoop. Share your thoughts,
            report bugs, suggest improvements, and help shape the future
            of preventive health education.
          </p>
          <Button
            size="sm"
            className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => openFeedbackModal('support_page')}
            data-testid="support-beta-feedback-btn"
          >
            Submit feedback
          </Button>
        </div>
      </div>
    </div>
  );
};

const SUPPORT_EMAIL = 'support@healthloop.app';

// ----------------------------------------------------------------------------
// Small, consistent surface primitives — used across all 4 sections so the
// page reads as ONE ecosystem rather than four glued-together pages.
// ----------------------------------------------------------------------------
const SectionHeader = ({ id, icon: Icon, eyebrow, title, subtitle, accent = 'indigo' }) => {
  const tones = {
    indigo: 'from-indigo-100 to-violet-100 text-indigo-700',
    blue: 'from-blue-100 to-sky-100 text-blue-700',
    purple: 'from-purple-100 to-fuchsia-100 text-purple-700',
    orange: 'from-orange-100 to-amber-100 text-orange-700',
  };
  return (
    <div id={id} className="scroll-mt-24">
      <div className="flex items-center gap-2.5 mb-1">
        <span className={`inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br ${tones[accent]} shadow-sm ring-1 ring-white`}>
          {Icon && <Icon className="h-4 w-4" />}
        </span>
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400">
            {eyebrow}
          </p>
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">{title}</h2>
        </div>
      </div>
      {subtitle && <p className="text-sm text-gray-500 mt-1 ml-12">{subtitle}</p>}
    </div>
  );
};

const SurfaceCard = ({ className = '', children, ...props }) => (
  <Card
    className={`border border-gray-100 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)] rounded-2xl bg-white motion-safe:transition-all ${className}`}
    {...props}
  >
    {children}
  </Card>
);

const FieldLabel = ({ children }) => (
  <label className="text-xs font-semibold text-gray-700 mb-1 block">{children}</label>
);

const SuccessTile = ({ title, message }) => (
  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center" role="status">
    <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
    <p className="text-sm font-semibold text-emerald-900">{title}</p>
    <p className="text-xs text-emerald-800 mt-1">{message}</p>
  </div>
);

// ----------------------------------------------------------------------------
// Main page
// ----------------------------------------------------------------------------
const SupportHelp = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Copy email function
  const copyEmail = () => {
    navigator.clipboard.writeText(SUPPORT_EMAIL);
    toast.success(t('supportDeep.emailCopied'));
  };

  // Report Problem state
  const [problemForm, setProblemForm] = useState({
    title: '',
    description: '',
    category: 'bug',
  });
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);

  // Contact Support state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  // Call Request state
  const [callRequestForm, setCallRequestForm] = useState({
    name: '',
    phone: '',
    preferred_time: '',
    reason: '',
  });
  const [callRequestSubmitting, setCallRequestSubmitting] = useState(false);
  const [callRequestSuccess, setCallRequestSuccess] = useState(false);

  // FAQ state
  const [expandedFaqCategory, setExpandedFaqCategory] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [faqSearchQuery, setFaqSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  // Deep-link: smooth-scroll to #faq / #contact / #health / #technical on mount.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const hash = window.location.hash?.replace('#', '');
    if (!hash) return;
    const t = window.setTimeout(() => {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => window.clearTimeout(t);
  }, []);

  // Silent device/browser fingerprint for technical reports.
  const getDeviceInfo = () => ({
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    timestamp: new Date().toISOString(),
  });

  // ----- Submit handlers (identical to previous implementation) -----
  const handleReportProblem = async (e) => {
    e.preventDefault();
    if (!problemForm.title.trim() || !problemForm.description.trim()) {
      toast.error(t('supportDeep.fillAllFields'));
      return;
    }
    setReportSubmitting(true);
    try {
      await healthApi.createSupportTicket({
        type: 'problem_report',
        category: problemForm.category,
        title: problemForm.title,
        description: problemForm.description,
        device_info: getDeviceInfo(),
        source_page: typeof window !== 'undefined' ? window.location.pathname : null,
        priority: 'medium',
      });
      setReportSuccess(true);
      setProblemForm({ title: '', description: '', category: 'bug' });
      toast.success(t('supportDeep.problemReportSubmittedToast'));
      setTimeout(() => setReportSuccess(false), 5000);
    } catch (error) {
      console.error('Error submitting problem report:', error);
      toast.error(t('supportDeep.submitFailGeneric', { email: SUPPORT_EMAIL }));
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleContactSupport = async (e) => {
    e.preventDefault();
    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
      toast.error(t('supportDeep.fillAllFields'));
      return;
    }
    setContactSubmitting(true);
    try {
      await healthApi.createSupportTicket({
        type: 'contact_support',
        title: contactForm.subject || 'General Inquiry',
        description: contactForm.message,
        contact_name: contactForm.name,
        contact_email: contactForm.email,
        source_page: typeof window !== 'undefined' ? window.location.pathname : null,
        device_info: getDeviceInfo(),
        priority: 'low',
      });
      setContactSuccess(true);
      setContactForm({ name: '', email: '', subject: '', message: '' });
      toast.success(t('supportDeep.messageSentToast'));
      setTimeout(() => setContactSuccess(false), 5000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error(t('supportDeep.submitFailContact', { email: SUPPORT_EMAIL }));
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleCallRequest = async (e) => {
    e.preventDefault();
    if (!callRequestForm.name.trim() || !callRequestForm.phone.trim()) {
      toast.error(t('supportDeep.fillAllFields'));
      return;
    }
    setCallRequestSubmitting(true);
    try {
      await healthApi.createSupportTicket({
        type: 'call_request',
        title: 'Call Request',
        description: `Name: ${callRequestForm.name}\nPhone: ${callRequestForm.phone}\nPreferred Time: ${callRequestForm.preferred_time}\nReason: ${callRequestForm.reason}`,
        contact_name: callRequestForm.name,
        contact_email: callRequestForm.phone,
        priority: 'medium',
      });
      setCallRequestSuccess(true);
      setCallRequestForm({ name: '', phone: '', preferred_time: '', reason: '' });
      toast.success(t('supportDeep.callRequestedToast'));
      setTimeout(() => setCallRequestSuccess(false), 5000);
    } catch (error) {
      console.error('Error submitting call request:', error);
      toast.error(t('supportDeep.submitFailGeneric', { email: SUPPORT_EMAIL }));
    } finally {
      setCallRequestSubmitting(false);
    }
  };

  const handleFaqSearch = (query) => {
    setFaqSearchQuery(query);
    setSearchResults(query.trim() ? searchFAQs(query) : []);
  };

  const toggleFaqCategory = (categoryId) =>
    setExpandedFaqCategory(expandedFaqCategory === categoryId ? null : categoryId);
  const toggleFaq = (faqId) => setExpandedFaq(expandedFaq === faqId ? null : faqId);

  // Quick-nav target list — drives the in-page anchor bar so users orient
  // instantly. Single source of truth for the section spine.
  const quickNav = [
    { id: 'faq', label: t('support.navHelpCenter'), icon: HelpCircle },
    { id: 'contact', label: t('support.navContact'), icon: MessageCircle },
    { id: 'health', label: t('support.navHealth'), icon: Stethoscope },
    { id: 'technical', label: t('support.navTechnical'), icon: Bug },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-indigo-50/40">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md border-b border-gray-100 sticky top-0 md:top-16 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3.5">
            <div className="flex items-center space-x-3 min-w-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/settings')}
                className="mr-1"
                aria-label={t('supportDeep.backToSettings')}
                data-testid="support-back-btn"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <HealthLoopLogo size="sm" showText={false} />
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('support.title')}</h1>
                <p className="text-xs text-gray-500">{t('support.subtitle')}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-10 pb-24">
        {/* Beta Feedback — dedicated section per launch plan item #2.
            Lives at the top of the Support page so beta testers see it
            before any FAQ / ticket form. */}
        <BetaFeedbackSupportSection />

        {/* Privacy notice — single, slim, calm */}
        <div
          className="flex items-start gap-3 rounded-2xl bg-emerald-50/60 border border-emerald-100 px-4 py-3"
          data-testid="support-privacy-banner"
        >
          <Shield className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
          <p className="text-xs text-gray-700 leading-relaxed">
            {t('support.privacyBanner')}
          </p>
        </div>

        {/* Quick navigation — 4 anchor tiles, the spine of the page */}
        <nav data-testid="support-quicknav" aria-label={t('supportDeep.quickNavAria')}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {quickNav.map(({ id, label, icon: Icon }) => (
              <a
                key={id}
                href={`#${id}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="group flex items-center gap-2 rounded-2xl bg-white border border-gray-100 px-3 py-3 shadow-sm hover:shadow-md hover:border-indigo-200 motion-safe:transition-all active:scale-[0.98]"
                data-testid={`support-nav-${id}`}
              >
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-50 to-violet-50 text-indigo-600 group-hover:scale-105 motion-safe:transition-transform">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="text-sm font-semibold text-gray-800">{label}</span>
              </a>
            ))}
          </div>
        </nav>

        {/* ============================================================
            1. HELP CENTER — search + FAQ accordion
            ============================================================ */}
        <section className="space-y-4" data-testid="support-section-help">
          <SectionHeader
            id="faq"
            icon={HelpCircle}
            eyebrow={t('support.section1')}
            title={t('support.helpCenterTitle')}
            subtitle={t('support.helpCenterSubtitle')}
            accent="indigo"
          />

          {/* Search bar — sits near the top of this section, single source of search */}
          <SurfaceCard data-testid="support-faq-search-card">
            <CardContent className="p-4 sm:p-5">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="search"
                  placeholder={t('supportDeep.searchPlaceholder')}
                  value={faqSearchQuery}
                  onChange={(e) => handleFaqSearch(e.target.value)}
                  className="pl-10 h-11 rounded-xl"
                  aria-label={t('supportDeep.searchAria')}
                  data-testid="support-faq-search-input"
                />
              </div>

              {searchResults.length > 0 && (
                <div className="mt-4 space-y-2 max-h-96 overflow-y-auto" data-testid="support-faq-results">
                  <p className="text-xs font-medium text-gray-500">
                    {searchResults.length === 1
                      ? t('supportDeep.resultsCount', { n: searchResults.length })
                      : t('supportDeep.resultsCountPlural', { n: searchResults.length })}
                  </p>
                  {searchResults.map((faq) => (
                    <div key={faq.id} className="rounded-xl border border-gray-100 p-3 bg-gray-50/40">
                      <div className="flex items-start gap-2 mb-1">
                        <span className="text-base">{faq.categoryIcon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900">{faq.question}</p>
                          <Badge variant="outline" className="text-[10px] mt-1">{faq.category}</Badge>
                        </div>
                      </div>
                      <p className="text-xs text-gray-700 mt-2 ml-7">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </SurfaceCard>

          {/* FAQ category accordion — collapsed by default for minimal scrolling */}
          {!faqSearchQuery && (
            <div className="space-y-2.5" data-testid="support-faq-categories">
              {faqCategories.map((category) => (
                <SurfaceCard key={category.id}>
                  <button
                    type="button"
                    onClick={() => toggleFaqCategory(category.id)}
                    className="w-full text-left px-5 py-4 flex items-center justify-between hover:bg-gray-50 motion-safe:transition-colors rounded-2xl"
                    aria-expanded={expandedFaqCategory === category.id}
                    data-testid={`support-faq-category-${category.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xl">{category.icon}</span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">{category.name}</p>
                        <p className="text-xs text-gray-500">{t('supportDeep.questionsCount', { n: category.faqs.length })}</p>
                      </div>
                    </div>
                    {expandedFaqCategory === category.id ? (
                      <ChevronUp className="h-4 w-4 text-gray-400 shrink-0" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
                    )}
                  </button>

                  {expandedFaqCategory === category.id && (
                    <div className="border-t border-gray-100 px-5 pb-4 pt-1 space-y-1">
                      {category.faqs.map((faq) => (
                        <div key={faq.id} className="py-2">
                          <button
                            type="button"
                            onClick={() => toggleFaq(faq.id)}
                            className="w-full text-left flex items-start justify-between gap-2 group"
                            aria-expanded={expandedFaq === faq.id}
                          >
                            <p className="text-sm font-medium text-gray-800 group-hover:text-indigo-600 motion-safe:transition-colors">
                              {faq.question}
                            </p>
                            {expandedFaq === faq.id ? (
                              <ChevronUp className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                            ) : (
                              <ChevronDown className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                            )}
                          </button>
                          {expandedFaq === faq.id && (
                            <p className="text-sm text-gray-600 mt-2 pl-3 border-l-2 border-indigo-100">
                              {faq.answer}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </SurfaceCard>
              ))}
            </div>
          )}
        </section>

        {/* ============================================================
            2. CONTACT SUPPORT — chat, email, contact form, callback
            ============================================================ */}
        <section className="space-y-4" data-testid="support-section-contact">
          <SectionHeader
            id="contact"
            icon={MessageCircle}
            eyebrow={t('support.section2')}
            title={t('support.contactTitle')}
            subtitle={t('support.contactSubtitle')}
            accent="blue"
          />

          {/* Row 1 — Live Chat (top priority) + Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SurfaceCard data-testid="support-livechat-card">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-sky-100 text-blue-700 shadow-sm">
                    <MessageCircle className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{t('supportDeep.liveChatTitle')}</p>
                    <p className="text-xs text-gray-500">{t('supportDeep.liveChatDesc')}</p>
                  </div>
                </div>
                <Button
                  size="sm"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                  onClick={() => toast.info(t('supportDeep.liveChatSoonToast'), { duration: 4000 })}
                  data-testid="support-livechat-btn"
                >
                  {t('supportDeep.startChat')}
                </Button>
              </CardContent>
            </SurfaceCard>

            <SurfaceCard data-testid="support-email-card">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-teal-100 text-emerald-700 shadow-sm">
                    <Mail className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{t('supportDeep.emailUsTitle')}</p>
                    <p className="text-xs text-gray-500">{t('supportDeep.emailUsDesc')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="flex-1 truncate text-sm font-medium text-gray-800 select-all">
                    {SUPPORT_EMAIL}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={copyEmail}
                    className="h-9 px-3"
                    data-testid="support-email-copy-btn"
                  >
                    <Copy className="h-3.5 w-3.5 mr-1" /> {t('supportDeep.copy')}
                  </Button>
                </div>
              </CardContent>
            </SurfaceCard>
          </div>

          {/* Row 2 — Contact form + Callback form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <SurfaceCard data-testid="support-contact-form-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-600" /> {t('supportDeep.contactFormTitle')}
                </CardTitle>
                <CardDescription className="text-xs">{t('supportDeep.contactFormDesc')}</CardDescription>
              </CardHeader>
              <CardContent className="pt-2">
                {contactSuccess ? (
                  <SuccessTile title={t('supportDeep.messageSent')} message={t('supportDeep.messageSentDesc')} />
                ) : (
                  <form onSubmit={handleContactSupport} noValidate className="space-y-3" data-testid="support-contact-form">
                    <div>
                      <FieldLabel>{t('supportDeep.yourNameLabel')}</FieldLabel>
                      <Input
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        placeholder={t('supportDeep.namePlaceholder')}
                        className="h-10 text-sm rounded-xl"
                        required
                        data-testid="support-contact-name"
                      />
                    </div>
                    <div>
                      <FieldLabel>{t('supportDeep.yourEmailLabel')}</FieldLabel>
                      <Input
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        placeholder={t('supportDeep.emailPlaceholder')}
                        className="h-10 text-sm rounded-xl"
                        required
                        data-testid="support-contact-email"
                      />
                    </div>
                    <div>
                      <FieldLabel>{t('supportDeep.subjectOptional')}</FieldLabel>
                      <Input
                        value={contactForm.subject}
                        onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                        placeholder={t('supportDeep.subjectPlaceholder')}
                        className="h-10 text-sm rounded-xl"
                        data-testid="support-contact-subject"
                      />
                    </div>
                    <div>
                      <FieldLabel>{t('supportDeep.messageLabel')}</FieldLabel>
                      <Textarea
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        placeholder={t('supportDeep.messagePlaceholder')}
                        rows={3}
                        className="text-sm resize-none rounded-xl"
                        required
                        data-testid="support-contact-message"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={contactSubmitting}
                      className="w-full h-10 bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="support-contact-submit"
                    >
                      {contactSubmitting ? t('supportDeep.sending') : (<><Send className="h-4 w-4 mr-2" /> {t('supportDeep.sendMessage')}</>)}
                    </Button>
                  </form>
                )}
              </CardContent>
            </SurfaceCard>

            <SurfaceCard data-testid="support-callback-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <Phone className="h-4 w-4 text-emerald-600" /> {t('supportDeep.callbackTitle')}
                </CardTitle>
                <CardDescription className="text-xs">{t('supportDeep.callbackDesc')}</CardDescription>
              </CardHeader>
              <CardContent className="pt-2">
                {callRequestSuccess ? (
                  <SuccessTile title={t('supportDeep.callbackRequested')} message={t('supportDeep.callbackRequestedDesc')} />
                ) : (
                  <form onSubmit={handleCallRequest} noValidate className="space-y-3" data-testid="support-callback-form">
                    <div>
                      <FieldLabel>{t('supportDeep.yourNameLabel')}</FieldLabel>
                      <Input
                        value={callRequestForm.name}
                        onChange={(e) => setCallRequestForm({ ...callRequestForm, name: e.target.value })}
                        placeholder={t('supportDeep.namePlaceholder')}
                        className="h-10 text-sm rounded-xl"
                        required
                        data-testid="support-callback-name"
                      />
                    </div>
                    <div>
                      <FieldLabel>{t('supportDeep.phoneLabel')}</FieldLabel>
                      <Input
                        type="tel"
                        value={callRequestForm.phone}
                        onChange={(e) => setCallRequestForm({ ...callRequestForm, phone: e.target.value })}
                        placeholder={t('supportDeep.phonePlaceholder')}
                        className="h-10 text-sm rounded-xl"
                        required
                        data-testid="support-callback-phone"
                      />
                    </div>
                    <div>
                      <FieldLabel>{t('supportDeep.preferredTimeLabel')}</FieldLabel>
                      <Input
                        value={callRequestForm.preferred_time}
                        onChange={(e) => setCallRequestForm({ ...callRequestForm, preferred_time: e.target.value })}
                        placeholder={t('supportDeep.preferredTimePlaceholder')}
                        className="h-10 text-sm rounded-xl"
                        data-testid="support-callback-time"
                      />
                    </div>
                    <div>
                      <FieldLabel>{t('supportDeep.reasonLabel')}</FieldLabel>
                      <Textarea
                        value={callRequestForm.reason}
                        onChange={(e) => setCallRequestForm({ ...callRequestForm, reason: e.target.value })}
                        placeholder={t('supportDeep.reasonPlaceholder')}
                        rows={2}
                        className="text-sm resize-none rounded-xl"
                        data-testid="support-callback-reason"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={callRequestSubmitting}
                      className="w-full h-10 bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="support-callback-submit"
                    >
                      {callRequestSubmitting ? t('supportDeep.submitting') : (<><Phone className="h-4 w-4 mr-2" /> {t('supportDeep.requestCallback')}</>)}
                    </Button>
                  </form>
                )}
              </CardContent>
            </SurfaceCard>
          </div>
        </section>

        {/* ============================================================
            3. HEALTH SERVICES — clinical-toned, premium-distinct
            ============================================================ */}
        <section className="space-y-4" data-testid="support-section-health">
          <SectionHeader
            id="health"
            icon={Stethoscope}
            eyebrow={t('support.section3')}
            title={t('support.healthServicesTitle')}
            subtitle={t('support.healthServicesSubtitle')}
            accent="purple"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Medication Support — non-premium, indigo */}
            <Link to="/medications" className="block" data-testid="support-health-medication">
              <SurfaceCard className="h-full hover:border-indigo-200 hover:shadow-md cursor-pointer">
                <CardContent className="p-5 h-full flex flex-col">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-100 to-violet-100 text-indigo-700 shadow-sm mb-3">
                    <Pill className="h-5 w-5" />
                  </span>
                  <p className="text-sm font-semibold text-gray-900">{t('supportDeep.medicationSupport')}</p>
                  <p className="text-xs text-gray-500 mt-1 mb-4 flex-1">
                    {t('supportDeep.medicationSupportDesc')}
                  </p>
                  <span className="text-xs font-medium text-indigo-600">{t('supportDeep.goToMedications')}</span>
                </CardContent>
              </SurfaceCard>
            </Link>

            {/* Specialist Consultation — premium */}
            <Link to="/expert-support" className="block" data-testid="support-health-specialist">
              <SurfaceCard className="h-full hover:border-purple-200 hover:shadow-md cursor-pointer ring-1 ring-purple-100/60 bg-gradient-to-br from-white via-purple-50/30 to-white">
                <CardContent className="p-5 h-full flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-purple-100 to-fuchsia-100 text-purple-700 shadow-sm">
                      <UserCheck className="h-5 w-5" />
                    </span>
                    <Badge className="bg-purple-600 text-white text-[10px] px-2 py-0.5">{t('supportDeep.premium')}</Badge>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{t('supportDeep.specialistConsultation')}</p>
                  <p className="text-xs text-gray-500 mt-1 mb-4 flex-1">
                    {t('supportDeep.specialistConsultationDesc')}
                  </p>
                  <span className="text-xs font-medium text-purple-600">{t('supportDeep.connectExpert')}</span>
                </CardContent>
              </SurfaceCard>
            </Link>

            {/* Video Consult — premium */}
            <Link to="/expert-support" className="block" data-testid="support-health-video">
              <SurfaceCard className="h-full hover:border-purple-200 hover:shadow-md cursor-pointer ring-1 ring-purple-100/60 bg-gradient-to-br from-white via-purple-50/30 to-white">
                <CardContent className="p-5 h-full flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-purple-100 to-fuchsia-100 text-purple-700 shadow-sm">
                      <Video className="h-5 w-5" />
                    </span>
                    <Badge className="bg-purple-600 text-white text-[10px] px-2 py-0.5">{t('supportDeep.premium')}</Badge>
                  </div>
                  <p className="text-sm font-semibold text-gray-900">{t('supportDeep.videoConsult')}</p>
                  <p className="text-xs text-gray-500 mt-1 mb-4 flex-1">
                    {t('supportDeep.videoConsultDesc')}
                  </p>
                  <span className="text-xs font-medium text-purple-600">{t('supportDeep.bookConsultation')}</span>
                </CardContent>
              </SurfaceCard>
            </Link>
          </div>

          {/* Medical-responsibility disclaimer */}
          <div
            className="flex items-start gap-2.5 rounded-xl bg-amber-50/60 border border-amber-100 px-4 py-3"
            data-testid="support-health-disclaimer"
          >
            <LifeBuoy className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-gray-700 leading-relaxed">
              {t('supportDeep.healthDisclaimer')}
            </p>
          </div>
        </section>

        {/* ============================================================
            4. TECHNICAL SUPPORT — bug report form
            ============================================================ */}
        <section className="space-y-4" data-testid="support-section-technical">
          <SectionHeader
            id="technical"
            icon={Bug}
            eyebrow={t('support.section4')}
            title={t('support.technicalTitle')}
            subtitle={t('support.technicalSubtitle')}
            accent="orange"
          />

          <SurfaceCard data-testid="support-bug-card">
            <CardContent className="p-5">
              {reportSuccess ? (
                <SuccessTile title={t('supportDeep.reportSubmitted')} message={t('supportDeep.reportSubmittedDesc')} />
              ) : (
                <form onSubmit={handleReportProblem} noValidate className="space-y-3" data-testid="support-bug-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <FieldLabel>{t('supportDeep.categoryLabel')}</FieldLabel>
                      <select
                        value={problemForm.category}
                        onChange={(e) => setProblemForm({ ...problemForm, category: e.target.value })}
                        className="w-full h-10 px-3 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-300"
                        data-testid="support-bug-category"
                      >
                        <option value="bug">{t('supportDeep.catBug')}</option>
                        <option value="performance">{t('supportDeep.catPerformance')}</option>
                        <option value="feature">{t('supportDeep.catFeature')}</option>
                        <option value="ui">{t('supportDeep.catUi')}</option>
                        <option value="other">{t('supportDeep.catOther')}</option>
                      </select>
                    </div>
                    <div>
                      <FieldLabel>{t('supportDeep.issueTitle')}</FieldLabel>
                      <Input
                        value={problemForm.title}
                        onChange={(e) => setProblemForm({ ...problemForm, title: e.target.value })}
                        placeholder={t('supportDeep.issueTitlePlaceholder')}
                        className="h-10 text-sm rounded-xl"
                        required
                        data-testid="support-bug-title"
                      />
                    </div>
                  </div>
                  <div>
                    <FieldLabel>{t('supportDeep.detailedDescription')}</FieldLabel>
                    <Textarea
                      value={problemForm.description}
                      onChange={(e) => setProblemForm({ ...problemForm, description: e.target.value })}
                      placeholder={t('supportDeep.detailedDescriptionPlaceholder')}
                      rows={5}
                      className="text-sm resize-none rounded-xl"
                      required
                      data-testid="support-bug-description"
                    />
                  </div>
                  <p className="text-[11px] text-gray-500">
                    {t('supportDeep.autoAttachNote')}
                  </p>
                  <Button
                    type="submit"
                    disabled={reportSubmitting}
                    className="w-full h-10 bg-orange-600 hover:bg-orange-700 text-white"
                    data-testid="support-bug-submit"
                  >
                    {reportSubmitting ? t('supportDeep.submitting') : (<><Send className="h-4 w-4 mr-2" /> {t('supportDeep.submitReport')}</>)}
                  </Button>
                </form>
              )}
            </CardContent>
          </SurfaceCard>
        </section>
      </div>

      <AppFooter />
    </div>
  );
};

export default SupportHelp;
